"use client";

import { useState } from "react";
import { motion, useScroll, useTransform, type Variants } from "framer-motion";
import { Send, MapPin, Mail, Clock } from "lucide-react";
import Image from "next/image";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, ease: "easeOut" },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const socials = [
  {
    label: "GitHub",
    href: "https://github.com",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
      </svg>
    ),
  },
  {
    label: "LinkedIn",
    href: "https://linkedin.com",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "Twitter / X",
    href: "https://twitter.com",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/",
    icon: (
      <svg width="18" height="18" viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g transform="translate(-700.000000, -360.000000)" fill="currentColor">
                <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z"></path>
            </g>
        </g>
      </svg>
    ),
  },
  {
    label: "Telegram",
    href: "https://t.me/",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.94z"/>
      </svg>
    ),
  },
];


export default function ContactClient() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate sumission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormState({ name: "", email: "", service: "", message: "" });
      setTimeout(() => setIsSuccess(false), 5000);
    }, 1500);
  };

  return (
    <div className="relative min-h-screen">
      {/* ── Fixed Parallax Aesthetic Background ── */}
      <div className="fixed inset-0 -z-10 bg-[var(--background)]">
        <motion.div
          className="absolute inset-0 h-[120vh] w-full"
          style={{ y: backgroundY }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--background)]/40 via-transparent to-[var(--background)]/90 mix-blend-multiply z-10" />
          <Image
            src="https://images.unsplash.com/photo-1770902634037-08b8e594168b?q=80&w=1974&auto=format&fit=crop"
            alt="Background"
            fill
            className="object-cover opacity-30 md:opacity-40 mix-blend-luminosity grayscale-[30%]"
          />
        </motion.div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 py-32 sm:px-8 lg:py-40">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-12"
        >
          {/* Left Column: Contact Info */}
          <div className="flex flex-col justify-center">
            <motion.h1 variants={itemVariants} className="text-4xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl drop-shadow-md lg:max-w-2xl">
              Let&apos;s build something <span className="text-[var(--accent)]">extraordinary.</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="mt-6 max-w-xl text-base leading-relaxed text-[var(--foreground-muted)] drop-shadow-md">
              Got a project in mind, an idea to validate, or a legacy system to migrate? I&apos;m currently taking on new clients and would love to hear from you.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)] backdrop-blur-sm">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-[var(--foreground)]">Email Me</p>
                  <a href="mailto:hello@devbyskillz.com" className="text-sm text-[var(--foreground-muted)] hover:text-[var(--accent)] transition-colors">
                    hello@devbyskillz.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)] backdrop-blur-sm">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-[var(--foreground)]">Location</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Available Worldwide</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)] backdrop-blur-sm">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="text-sm font-bold text-[var(--foreground)]">Response Time</p>
                  <p className="text-sm text-[var(--foreground-muted)]">Usually within 24 hours</p>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-12">
              <p className="mb-4 text-sm font-bold text-[var(--foreground)] uppercase tracking-wider">Connect directly</p>
              <div className="flex flex-wrap gap-3">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 text-[var(--foreground)] backdrop-blur-md transition-all hover:-translate-y-1 hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-white/10 shadow-lg"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column: Contact Form */}
          <motion.div variants={itemVariants} className="relative w-full max-w-xl mx-auto lg:ml-auto lg:mr-0">
            {/* Very subtle glow behind the form frame for depth without solid borders */}
            <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-br from-[var(--accent)]/20 via-transparent to-transparent opacity-50 blur-xl" />
            
            <div className="relative rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-xl sm:p-10 shadow-2xl">
              <h2 className="mb-6 text-2xl font-bold text-[var(--foreground)]">Send a message</h2>
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm font-semibold text-[var(--foreground)]">
                    Name <span className="text-[var(--accent)]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    placeholder="John Doe"
                    className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-[var(--foreground)] placeholder:text-[var(--foreground-muted)]/50 focus:border-[var(--accent)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm font-semibold text-[var(--foreground)]">
                    Email <span className="text-[var(--accent)]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    placeholder="john@example.com"
                    className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-[var(--foreground)] placeholder:text-[var(--foreground-muted)]/50 focus:border-[var(--accent)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="service" className="text-sm font-semibold text-[var(--foreground)]">
                    Service Needed
                  </label>
                  <select
                    id="service"
                    value={formState.service}
                    onChange={(e) => setFormState({ ...formState, service: e.target.value })}
                    className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all appearance-none"
                  >
                    <option value="" className="bg-[var(--background)]">Select a service...</option>
                    <option value="web-dev" className="bg-[var(--background)]">Custom Web Development</option>
                    <option value="ecommerce" className="bg-[var(--background)]">E-commerce Store</option>
                    <option value="app-dev" className="bg-[var(--background)]">Web / Mobile App</option>
                    <option value="migration" className="bg-[var(--background)]">Platform Migration</option>
                    <option value="other" className="bg-[var(--background)]">Other / Consultation</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-sm font-semibold text-[var(--foreground)]">
                    Project Details <span className="text-[var(--accent)]">*</span>
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={formState.message}
                    onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                    placeholder="Tell me a bit about your project goals, timeline, or requirements..."
                    className="resize-none rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-[var(--foreground)] placeholder:text-[var(--foreground-muted)]/50 focus:border-[var(--accent)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)] transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-[var(--accent)] px-8 py-4 text-sm font-bold text-white shadow-lg transition-all hover:bg-[var(--accent)]/90 hover:shadow-xl hover:shadow-[var(--accent)]/30 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {isSubmitting ? "Sending..." : isSuccess ? "Message Sent!" : "Send Message"}
                    {!isSubmitting && !isSuccess && <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />}
                  </span>
                  <div className="absolute inset-0 z-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 transition-opacity duration-500 group-hover:animate-shimmer group-hover:opacity-100" />
                </button>
                
                {isSuccess && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-sm font-medium text-green-400 mt-2"
                  >
                    Thank you! I&apos;ll be in touch soon.
                  </motion.p>
                )}
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
